import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaStar,
  FaShareAlt,
  FaBookmark,
  FaFlag,
  FaArrowLeft,
  FaArrowRight,
  FaExpand,
  FaChevronLeft
} from "react-icons/fa";
import { db } from "../../../Components/firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";

// Context & Hooks
import { useAuth } from "../../../AuthContext";
import { useCheckForAffiliateLink } from "../../../Hooks/useCheckForAffiliateLink";
import useIsItemInCart from "../../../Hooks/useIsItemInCart";

// Components
import PageHeader from "../../../Components/PageHeader";
import SellerRating from "../../../Components/Reviews/SellerRating";
import ViewPhotoOverlay from "../../../Components/Overlays/ViewPhotoOverlay";
import PostSectionReviews from "../../../Components/PostSectionReviews";
import PostDropMenu from "../../../Components/PostDropMenu";
import ItemInCartOverlay from "../../../Components/Overlays/ItemInCartOverlay";
import MainCommentsSection from "../../../Components/CommentsMobile/CommentsSectionMobile";
import PromotedPanel from "../../../Components/ViewPostsMobile/PromotedPanelMobile";
import CreateAffiliateLinkOverlay from "../../../Components/Overlays/CreateAffiliateLinkOverlay";
import BlockUserOverlay from "../../../Components/Overlays/BlockUserOverlay";
import DeletePostOverlay from "../../../Components/Overlays/DeletePostOverlay";
import GetPostRoute from "../../../Functions/GetPostRoute";
import ViewRequestedQuotesMobile from "./ViewRequestedQuotesMobile";
import GetQuoteOverlay from "../../../Components/Overlays/GetQuoteOverlay";

import "./DirectoryPostMobile.css";

const DirectoryPostMobile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // States
  const [post, setPost] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [notification, setNotification] = useState("");
  const [bookmarked, setBookmarked] = useState(false);
  const [reported, setReported] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [addQuoteToCartHandler, setAddQuoteToCartHandler] = useState(null);
  const [showGetQuoteOverlay, setShowGetQuoteOverlay] = useState(false);
  
  // Overlay States
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayIndex, setOverlayIndex] = useState(0);
  const [showCartOverlay, setShowCartOverlay] = useState(false);
  const [showAffiliateLinkOverlay, setShowAffiliateLinkOverlay] = useState(false);
  const [showBlockUserOverlay, setShowBlockUserOverlay] = useState(false);
  const [showDeletePostOverlay, setShowDeletePostOverlay] = useState(false);

  const { affiliateDocId: affiliateLinkId } = useCheckForAffiliateLink(post?.userId);
  const { isInCart } = useIsItemInCart(id);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const postRef = doc(db, "Posts", id);
        const postSnap = await getDoc(postRef);
        
        if (!postSnap.exists()) {
          navigate("/home");
          return;
        }

        const postData = postSnap.data();
        if (postData.type !== 'directory') {
          const postRoute = GetPostRoute(postData.type);
          navigate(postRoute ? postRoute + id : "/home");
          return;
        }
        
        setPost(postData);

        // Fetch Seller
        const userRef = doc(db, "Users", postData.userId);
        const userSnap = await getDoc(userRef);
        setSeller(userSnap.exists() ? userSnap.data() : null);

        // Auth Checks
        if (currentUser) {
          const currentUserRef = doc(db, "Users", currentUser.uid);
          const currentUserSnap = await getDoc(currentUserRef);
          if (currentUserSnap.exists()) {
            setBookmarked(currentUserSnap.data().bookmarks?.includes(id) || false);
          }
          setReported(postData.report?.includes(currentUser.uid) || false);
        }
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, currentUser, navigate]);

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 3000);
  };

  const handleAddToCart = async (quoteData = selectedQuote) => {
    if (!currentUser) {
      alert("Login required");
      return;
    }

    try {
      const cartRef = collection(db, "Users", currentUser.uid, "cart");
      const itemRef = doc(cartRef, id);

      const existing = await getDoc(itemRef);

      if (existing.exists()) {
        alert("Already in cart");
        return;
      }

      await setDoc(itemRef, {
        postId: id,
        title: post.title || "Untitled",
        description: post.description || "No Description Available.",
        type: post.type,
        price: quoteData?.quotePrice || 0,
        sellerId: post.userId,
        sellerName: seller?.username || "Unknown Seller",
        sellerAvatar:
          seller?.profilepic ||
          `${process.env.PUBLIC_URL}/default-profile.png`,
        image:
          post.images?.[0] ||
          `${process.env.PUBLIC_URL}/default-thumbnail.png`,
        reviews: post.sellerReviews || 0,
        affiliateLinkId: affiliateLinkId || null,
        addedAt: new Date(),
        requestedQuoteId: quoteData?.requestedQuoteId || null,
        vehicleInfo: quoteData?.vehicleInfo || null,
        additionalInfo: quoteData?.additionalInfo || null,
        quoteImages:
          quoteData?.images ||
          `${process.env.PUBLIC_URL}/default-thumbnail.png`,
      });

      setShowCartOverlay(true);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setAddQuoteToCartHandler(() => handleAddToCart);
  }, [post, seller, currentUser, selectedQuote]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    showNotification("Link copied!");
  };

  const handleReport = async () => {
    if (!currentUser) return;
    const postRef = doc(db, "Posts", id);
    try {
      const postSnap = await getDoc(postRef);
      if (!postSnap.exists()) return;

      const postData = postSnap.data();
      const reportArray = postData.report || [];

      if (reportArray.includes(currentUser.uid)) {
        await updateDoc(postRef, { report: arrayRemove(currentUser.uid) });
        setReported(false);
        showNotification("Report removed");
      } else {
        await updateDoc(postRef, { report: arrayUnion(currentUser.uid) });
        setReported(true);
        showNotification("Reported");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmDelete = async (id) => {
    try {
      // Delete the post from Firestore
      await deleteDoc(doc(db, "Posts", id));
      showNotification("Post deleted successfully!");
      setShowDeletePostOverlay(false);
      navigate("/"); // or redirect wherever
    } catch (err) {
      console.error("Error deleting post:", err);
      showNotification("Failed to delete post");
    }
  };

  if (loading) return <div className="dpm-loader">Loading...</div>;
  if (!post) return null;

  const images = post.images?.length > 0 ? post.images : ["/default-thumbnail.png"];
  const isSeller = currentUser?.uid === post.userId;

  const formatLink = (url) =>
    url ? (url.startsWith("http") ? url : `https://${url}`) : null;

  const isAddToCartDisabled =
    !currentUser ||
    !post ||
    currentUser.uid === post.userId ||
    isInCart;

  const disabledReason =
    !currentUser
      ? "Login to get quote"
      : !post
      ? ""
      : currentUser.uid === post.userId
      ? "You can’t request quotes on your own post"
      : isInCart
      ? "Item already in cart"
      : "";

  return (
    <div className="dpm-container">
      {notification && <div className="dpm-toast">{notification}</div>}
      
      <PageHeader 
        title="Directory Post" 
        backgroundUrl="https://assets.goaaa.com/image/upload/w_2880,c_fill,q_auto,f_auto/v1742591982/AAAGilbert-428_retouched.jpg" 
      />

      {/* Header Info */}
      <div className="dpm-header-actions">
        <span className="dpm-breadcrumb">VRUMIES / DIRECTORY</span>
        <PostDropMenu 
          canDelete={isSeller} 
          onDelete={() => setShowDeletePostOverlay(true)} 
          canBlock={!isSeller} 
          onBlock={() => setShowBlockUserOverlay(true)} 
          canAffiliate={!isSeller} 
          onAffiliate={() => setShowAffiliateLinkOverlay(true)}
          canReport={true}
          onReport={handleReport}
          reported={reported} 
        />
      </div>

      {/* Image Carousel */}
      <div className="dpm-image-section">
        <div className="dpm-main-image-wrapper">
          <img 
            src={images[currentImage]} 
            alt="Product" 
            className="dpm-main-image"
            onClick={() => {
              setOverlayIndex(currentImage);
              setShowOverlay(true);
            }}
          />
          <div className="dpm-image-counter">{currentImage + 1} / {images.length}</div>
        </div>
        <div className="dpm-thumbnails">
          {images.map((img, idx) => (
            <img 
              key={idx} 
              src={img} 
              className={`dpm-thumb ${idx === currentImage ? "active" : ""}`} 
              onClick={() => setCurrentImage(idx)}
              alt="thumb"
            />
          ))}
        </div>
      </div>

      {/* Content Body */}
      <div className="dpm-body">
        <h1 className="dpm-title">{post.title?.toUpperCase()}</h1>
        <div className="dpm-price-row">
          <div className="dpm-price-wrapper">
            <span className="dpm-starting-price-label">
              Starting At
            </span>

            <div className="dpm-price-tag">
              {post.price !== undefined
                ? `~$${post.price.toFixed(2)}`
                : "Price N/A"}
            </div>
          </div>
        </div>
        
        <div className="dpm-seller-card">
          <Link to={`/viewprofilemobile/${post.userId}`}>
            <img src={seller?.profilepic || "/default-profile.png"} className="dpm-seller-avatar" alt="seller" />
          </Link>
          <div className="dpm-seller-info">
            <Link to={`/viewprofilemobile/${post.userId}`} className="dpm-seller-name">{seller?.username}</Link>
            <SellerRating userId={post.userId} />
          </div>
          <button className={`dpm-follow-btn ${followed ? 'active' : ''}`} onClick={() => setFollowed(!followed)}>
            {followed ? "Following" : "Follow"}
          </button>
        </div>

        {/* Tabs */}
        <div className="dpm-tabs">
          {["description", "details", "reviews"].map(tab => (
            <div 
              key={tab}
              className={`dpm-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.toUpperCase()}
            </div>
          ))}
        </div>

        <div className="dpm-tab-content">
          {activeTab === "description" && <p>{post.description}</p>}
          {activeTab === "details" && (
            <div className="dpm-details-list">
              <p><strong>Tokens:</strong> {post.tokens ?? "N/A"}</p>
              <p><strong>Post Location:</strong> {post.location ?? "N/A"}</p>
              <p><strong>Link:</strong>{" "}
                {post.link ? (
                  <a href={formatLink(post.link)} target="_blank" rel="noopener noreferrer">
                    {post.link}
                  </a>
                ) : "N/A"}
              </p>
              <p><strong>Service Location:</strong>{" "} {post.serviceLocation ?? "N/A"}</p>
              {post.serviceLocation === "Business Address" && (
              <p><strong>Business Address:</strong>{" "} {post.businessAddress ?? "N/A"}</p>
              )}
            </div>
          )}
          {activeTab === "reviews" && <PostSectionReviews userId={post.userId} />}
        </div>

        {/* Action Grid */}
        <div className="dpm-action-grid">
          <button className="dpm-grid-btn" onClick={handleShare}><FaShareAlt /> Share</button>
          <button className={`dpm-grid-btn ${bookmarked ? 'active' : ''}`}><FaBookmark /> Bookmark</button>
          <button className="dpm-grid-btn"><FaFlag /> Report</button>
        </div>

        <button
          className="dpm-add-cart-btn"
          onClick={() => setShowGetQuoteOverlay(true)}
          disabled={isAddToCartDisabled || loading}
          title={disabledReason}
        >
          {isInCart
            ? "✓ IN CART"
            : isSeller
            ? "YOUR POST"
            : "GET QUOTE"}
        </button>

        <hr className="dpm-divider" />

        <ViewRequestedQuotesMobile
          directoryPostId={id}
          sellerUserId={post.userId}
          onSelectQuote={setSelectedQuote}
          onAddQuoteToCart={addQuoteToCartHandler}
        />

        <div className="dpm-comments-section">
          <h3>Comments</h3>
          <MainCommentsSection postId={id} />
        </div>

        <div className="dpm-promoted-section">
          <h3>Promoted</h3>
          <PromotedPanel category="directory" />
        </div>
      </div>

      {/* Overlays */}
      {showOverlay && (
        <ViewPhotoOverlay
          photos={images}
          startIndex={overlayIndex}
          caption={post.title}
          createdAt={post.createdAt}
          onClose={() => {
            setShowOverlay(false);
            setOverlayIndex(0);
          }}
        />
      )}
      {showCartOverlay && (
        <ItemInCartOverlay productName={post.title} onClose={() => setShowCartOverlay(false)} />
      )}
      {/* ... Other overlays follow same pattern ... */}
      {showAffiliateLinkOverlay && (
        <CreateAffiliateLinkOverlay
          postId={id}
          isOpen={showAffiliateLinkOverlay}
          onClose={() => setShowAffiliateLinkOverlay(false)}
        />
      )}
      {showBlockUserOverlay && (
        <BlockUserOverlay
          userId={post.userId}
          from="post"
          isOpen={showBlockUserOverlay}
          onClose={() => setShowBlockUserOverlay(false)}
        />
      )}
      {showDeletePostOverlay && (
        <DeletePostOverlay
          postId={id}
          isOpen={showDeletePostOverlay}
          onClose={() => setShowDeletePostOverlay(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {showGetQuoteOverlay && (
        <GetQuoteOverlay
          postId={id}
          onClose={() => setShowGetQuoteOverlay(false)}
        />
      )}
    </div>
  );
};

export default DirectoryPostMobile;